import React, { useState, useEffect, useCallback } from 'react'
import { Modal, Button, Alert, Form, ListGroup, Badge, Spinner } from 'react-bootstrap'
import { stageTasksAPI } from '../../services/api'

const StageTasksModal = ({ show, onHide, workStage, projectId }) => {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(false)
  const [newTitle, setNewTitle] = useState('')
  const [saving, setSaving] = useState(false)

  const loadTasks = useCallback(async () => {
    setLoading(true)
    try {
      const response = await stageTasksAPI.list(projectId, workStage.id)
      setTasks(response.data.tasks)
    } catch (error) {
      console.error('Ошибка загрузки задач:', error)
    } finally {
      setLoading(false)
    }
  }, [projectId, workStage?.id])

  useEffect(() => {
    if (show && workStage) {
      loadTasks()
    }
  }, [show, workStage, loadTasks])

  if (!workStage) return null

  const handleCreate = async (e) => {
    e.preventDefault()
    const title = newTitle.trim()
    if (!title) return

    setSaving(true)
    try {
      const response = await stageTasksAPI.create(projectId, workStage.id, title)
      setTasks((prev) => [...prev, response.data])
      setNewTitle('')
    } catch (error) {
      console.error('Ошибка создания задачи:', error)
    } finally {
      setSaving(false)
    }
  }

  const handleToggle = async (task) => {
    try {
      const response = await stageTasksAPI.toggleCompletion(projectId, workStage.id, task.id)
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, completed: response.data.completed } : t))
      )
    } catch (error) {
      console.error('Ошибка изменения статуса задачи:', error)
    }
  }

  const handleDelete = async (task) => {
    if (!window.confirm('Удалить задачу?')) return
    try {
      await stageTasksAPI.delete(projectId, workStage.id, task.id)
      setTasks((prev) => prev.filter((t) => t.id !== task.id))
    } catch (error) {
      console.error('Ошибка удаления задачи:', error)
    }
  }

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton className="bg-secondary bg-opacity-10 border-0 pb-3">
        <div className="d-flex flex-column">
          <Modal.Title className="mb-1 fw-bold">
            <i className="bi bi-list-check me-2"></i>
            Задачи этапа
          </Modal.Title>
          <small className="text-muted">{workStage.work_stage_name}</small>
        </div>
      </Modal.Header>
      <Modal.Body className="p-4">
        <Form onSubmit={handleCreate} className="d-flex gap-2 mb-4">
          <Form.Control
            type="text"
            placeholder="Новая задача"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <Button type="submit" variant="primary" disabled={saving || !newTitle.trim()}>
            Добавить
          </Button>
        </Form>

        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" variant="secondary" />
          </div>
        ) : tasks.length > 0 ? (
          <ListGroup>
            {tasks.map((task) => (
              <ListGroup.Item
                key={task.id}
                className="d-flex align-items-center justify-content-between gap-2"
              >
                <span className={task.completed ? 'text-decoration-line-through text-muted' : ''}>
                  {task.title}
                </span>
                <div className="d-flex align-items-center gap-2 flex-shrink-0">
                  <Badge
                    bg={task.completed ? 'success' : 'warning'}
                    text={task.completed ? undefined : 'dark'}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleToggle(task)}
                    title="Нажмите, чтобы изменить статус"
                  >
                    {task.completed ? 'Готова' : 'В работе'}
                  </Badge>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleDelete(task)}
                  >
                    <i className="bi bi-trash"></i>
                  </Button>
                </div>
              </ListGroup.Item>
            ))}
          </ListGroup>
        ) : (
          <Alert variant="light" className="border text-center py-4 mb-0">
            <i className="bi bi-list-check fs-3 text-muted d-block mb-2"></i>
            <p className="mb-0 text-muted">Задач для этого этапа пока нет</p>
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer className="border-0 pt-0 pb-4 px-4">
        <Button variant="outline-secondary" onClick={onHide}>
          <i className="bi bi-x-lg me-2"></i>
          Закрыть
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default StageTasksModal
